/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useEffect, useRef } from 'react';

import { announce } from 'sarsaparilla';

import { runRateLimitReload } from './config';

/**
 * The points in the wait worth saying out loud, and what is said at each.
 *
 * The wording is spoken only, and reads differently from the notice's visible copy on
 * purpose: words rather than `m:ss`, and a statement rather than a value a screen
 * reader would have to re-read in full to convey one change.
 *
 * Ascending, so a countdown that crosses several at once — a throttled background tab
 * firing one late tick — announces the smallest, which is the only one still true.
 */
const MILESTONES = [
    { ms: 10_000, message: 'Ten seconds until this page refreshes itself.' },
    { ms: 30_000, message: 'Thirty seconds until this page refreshes itself.' },
    { ms: 60_000, message: 'One minute until this page refreshes itself.' },
];

const reloadMessage = 'Refreshing the page now.';

/**
 * Announces the remaining wait at a few milestones, using the same mechanism as the
 * cart timer.
 *
 * Milestones rather than a live region on the ticking value — the README's "Working
 * without looking at it" has the argument.
 *
 * A milestone announces on the crossing rather than the value, so the value the notice
 * opened on is never repeated straight back: the alert dialog already read the whole
 * notice out, countdown line included. That is why the common one-minute wait
 * announces at thirty and ten seconds but not at its own first second.
 *
 * Zero is left to `announceAndReload`, which says something more useful than a
 * remaining wait of nothing.
 *
 * @param remainingMs the countdown's current value, or null while there is none
 */
export function useCountdownAnnouncements(remainingMs: number | null) {
    const previousRef = useRef<number | null>(null);

    useEffect(() => {
        const previous = previousRef.current;
        previousRef.current = remainingMs;

        // Nothing has been crossed yet on the first value, and nothing is counting
        // down at all when it is null
        if (previous === null || remainingMs === null) {
            return;
        }

        const milestone = MILESTONES.find(
            ({ ms }) => previous > ms && remainingMs <= ms && remainingMs > 0
        );

        if (milestone) {
            announce(milestone.message);
        }
    }, [remainingMs]);
}

/**
 * Announces the reload and then performs it.
 *
 * The one moment in the notice's life that genuinely needs announcing: focus and
 * reading position are about to vanish, and without this they do so with no
 * explanation. Assertive because it describes something already happening, and
 * because a polite announcement can be dropped by the navigation it is about.
 *
 * Used by both ways out — the countdown reaching zero and the manual reload button —
 * so neither can navigate silently.
 */
export function announceAndReload() {
    announce(reloadMessage, 'assertive');
    runRateLimitReload();
}
